/**
 * Default stencil declarations
 */

import _ from 'lodash'

import API from '@/util/api/API'

import { EDITOR_SET_FIELD_DATA } from '@/store/editor/constants'
import { StencilSet } from '@/store/editor/types'

const stencils: StencilSet = {

    // stencil key (match API)
    website: {
        // type title
        title: 'Website',
        // initial icon
        defaultIcon: 'globe-americas',
        // fieldset
        fields: {
            // title is a special key, and does
            // not match the api spec
            title: {
                name: 'Title',
                input: {
                    copy: false
                },
                required: true
            },
            // field key (match API)
            url: {
                // display name
                name: 'URL',
                // input with options
                input: {
                    // vue component
                    // add link action
                    link: true,
                    // on change listener
                    // return false to cancel input
                    onChange: (oldVal, newVal, $vue) => {
                        const snip = (url: string): string => (url
                            ? url.replace(/https?:\/\//, '').replace(/\/.*/, '')
                            : '')
                        const domain = $vue.$store.state.editor.entry.fields.domain.value
                        if (!oldVal || snip(oldVal) === domain) {
                            $vue.$store.commit(EDITOR_SET_FIELD_DATA, {
                                key: 'domain',
                                value: snip(newVal)
                            })
                        }
                        return true
                    }
                }
            },
            domain: {
                name: 'Domain',
                input: {
                    // add tooltip value
                    tooltip: 'Easily categorize and autofill passwords with matching domain names'
                }
            },
            username: {
                name: 'Username'
                // input without options
            },
            password: {
                name: 'Password',
                input: {
                    type: 'password',
                    generate: true
                },
                required: true
            },
            notes: {
                name: 'Notes',
                input: {
                    // use full container width on large screens
                    fullWidth: true,
                    // height of component (in lines)
                    minHeight: 3,
                    maxHeight: 12
                }
            }
        }

    },

    vault: {
        title: 'Vault',
        defaultIcon: 'folder',
        fields: {
            title: {
                name: 'Title',
                input: {
                    copy: false
                },
                required: true
            },
            notes: {
                name: 'Notes',
                input: {
                    fullWidth: true,
                    minHeight: 3,
                    maxHeight: 12
                }
            }
        }
    },

    note: {
        title: 'Note',
        defaultIcon: 'file',
        fields: {
            title: {
                name: 'Title',
                input: {
                    copy: false
                },
                required: true
            },
            notes: {
                name: 'Notes',
                input: {
                    // use full container width on large screens
                    fullWidth: true,
                    // min/max height of component (in lines)
                    minHeight: 3,
                    maxHeight: 12
                }
            }
        }
    },

    ssh: {
        title: 'SSH',
        defaultIcon: 'terminal',
        fields: {
            title: {
                name: 'Title',
                input: {
                    copy: false
                },
                required: true
            },
            host: {
                name: 'Hostname',
                input: {
                    link: true,
                    uriSchema: 'ssh',
                    onChange: _.debounce(async (oldVal, newVal, $vue) => {
                        let ip = $vue.$store.state.editor.entry.fields.ip.value
                        if (newVal && !ip) {
                            const snip = (url: string): string => (url
                                ? url.replace(/^.*:\/\//, '').replace(/[/:].*/, '')
                                : '')
                            const result = await API.get(`util/resolve/${snip(newVal)}`)
                            // reload ip, make sure it's still empty
                            ip = $vue.$store.state.editor.entry.fields.ip.value
                            if (result.success && !ip) {
                                $vue.$store.commit(EDITOR_SET_FIELD_DATA, {
                                    key: 'ip',
                                    value: result.ip
                                })
                            }
                        }
                        return true
                    }, 1000)
                },
                required: true
            },
            ip: {
                name: 'IP Address',
                input: {
                    link: true,
                    uriSchema: 'ssh'
                },
                required: true
            },
            username: {
                name: 'Username',
                required: true
            },
            password: {
                name: 'Password',
                input: {
                    type: 'password',
                    generate: true
                },
                required: true
            },
            private_key: {
                name: 'Private Key',
                input: {
                    type: 'password',
                    fullWidth: true,
                    minHeight: 3,
                    maxHeight: 12,
                    tooltip: 'Share your private key to allow other team members to SSH in securely'
                }
            },
            notes: {
                name: 'Notes',
                input: {
                    fullWidth: true,
                    minHeight: 3,
                    maxHeight: 12
                }
            }
        }
    },

    ftp: {
        title: 'FTP',
        defaultIcon: 'hdd',
        fields: {
            title: {
                name: 'Title',
                input: {
                    copy: false
                },
                required: true
            },
            host: {
                name: 'Host'
            },
            username: {
                name: 'Username',
                required: true
            },
            password: {
                name: 'Password',
                input: {
                    type: 'password',
                    generate: true
                },
                required: true
            },
            notes: {
                name: 'Notes',
                input: {
                    fullWidth: true,
                    minHeight: 3,
                    maxHeight: 12
                }
            }
        }
    },

    database: {
        title: 'Database',
        defaultIcon: 'database',
        fields: {
            title: {
                name: 'Title',
                input: {
                    copy: false
                },
                required: true
            },
            host: {
                name: 'Host'
            },
            database: {
                name: 'Database Name',
                required: true
            },
            username: {
                name: 'Username',
                required: true
            },
            password: {
                name: 'Password',
                input: {
                    type: 'password',
                    generate: true
                },
                required: true
            },
            notes: {
                name: 'Notes',
                input: {
                    fullWidth: true,
                    minHeight: 3,
                    maxHeight: 12
                }
            }
        }
    }

}

export default stencils
